// Une règle cashless (crédits ou unité A-D) : mode + jour(s) + valeur. Partagée par l'éditeur de
// formulaire du contact externe et par l'écran « règles par type d'accès » du chef de pôle, pour que
// les deux éditent les règles exactement de la même façon (même forme que config.rules côté API).
import React from 'react';
import {
  Stack, Typography, TextField, Select, MenuItem, FormControl, InputLabel, Checkbox, ListItemText,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';

export const emptyRule = () => ({ mode: 'none', value: 1, day: '', days: [], combinator: 'or' });

export const emptyCashlessRules = () => ({
  credit: emptyRule(),
  units: { A: emptyRule(), B: emptyRule(), C: emptyRule(), D: emptyRule() },
});

// Règles reçues du serveur (éventuellement partielles) complétées par des règles vides.
export const withDefaultRules = (rules) => ({
  credit: { ...emptyRule(), ...(rules?.credit || {}) },
  units: {
    A: { ...emptyRule(), ...(rules?.units?.A || {}) },
    B: { ...emptyRule(), ...(rules?.units?.B || {}) },
    C: { ...emptyRule(), ...(rules?.units?.C || {}) },
    D: { ...emptyRule(), ...(rules?.units?.D || {}) },
  },
});

const RULE_MODES = [
  { value: 'none', label: 'Aucun' },
  { value: 'fixed', label: 'Valeur fixe' },
  { value: 'perDay', label: 'Par jour de présence' },
  { value: 'day', label: 'Jour précis' },
  { value: 'days', label: 'Plusieurs jours (ET/OU)' },
];

/** `onChange(fields)` reçoit uniquement les champs modifiés de la règle. */
const CashlessRuleRow = ({ label, rule, dayLabels, onChange }) => (
  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 1.5 }}>
    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 90 }}>{label}</Typography>
    <FormControl size="small" sx={{ minWidth: 180 }}>
      <InputLabel>Mode</InputLabel>
      <Select label="Mode" value={rule.mode} onChange={(e) => onChange({ mode: e.target.value })}>
        {RULE_MODES.map((m) => (
          <MenuItem
            key={m.value}
            value={m.value}
            disabled={m.value !== 'none' && m.value !== 'fixed' && dayLabels.length === 0}
          >
            {m.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    {rule.mode === 'day' && (
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Jour</InputLabel>
        <Select label="Jour" value={rule.day || ''} onChange={(e) => onChange({ day: e.target.value })}>
          <MenuItem value=""><em>— Choisir —</em></MenuItem>
          {dayLabels.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
        </Select>
      </FormControl>
    )}
    {rule.mode === 'days' && (
      <>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Jours</InputLabel>
          <Select
            multiple
            label="Jours"
            value={rule.days || []}
            onChange={(e) => {
              const { value } = e.target;
              onChange({ days: typeof value === 'string' ? value.split(',') : value });
            }}
            renderValue={(selected) => selected.join(', ') || '— Choisir —'}
          >
            {dayLabels.map((l) => (
              <MenuItem key={l} value={l}>
                <Checkbox size="small" checked={(rule.days || []).includes(l)} />
                <ListItemText primary={l} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={rule.combinator || 'or'}
          onChange={(e, next) => { if (next) onChange({ combinator: next }); }}
        >
          <ToggleButton value="or">OU (au moins un)</ToggleButton>
          <ToggleButton value="and">ET (tous)</ToggleButton>
        </ToggleButtonGroup>
      </>
    )}
    {(rule.mode === 'fixed' || rule.mode === 'perDay' || rule.mode === 'day' || rule.mode === 'days') && (
      <TextField
        size="small"
        type="number"
        label="Valeur"
        inputProps={{ min: 0 }}
        value={rule.value}
        onChange={(e) => onChange({ value: e.target.value })}
        sx={{ width: 110 }}
      />
    )}
  </Stack>
);

export default CashlessRuleRow;
